import React, { useEffect, useState, useRef, useCallback } from "react";
import Footer from "./Footer";
import history from "../history";
import { Button, Col, Row, Container, Card, CardBody, Collapse } from "reactstrap";
import { Users, BookOpen, Shield, HeartHandshake, FileCheck, Stethoscope, Link, Lightbulb, Menu, X, ArrowLeft } from "lucide-react";
import "./Home.css";
import MelanomaEducation from "./MelanomaEducation";
import MelanomaLoginPopover, { MelanomaLoginForm } from "./MelanomaLoginPopover";
import Text from "../components/Text";
import Messages from "../Shared/Messages";
import I18nProvider from "../services/I18nProvider";

const intl = I18nProvider.getIntlProvider();

const Home = ({ test }) => {
  const [showContent, setShowContent] = useState(false);
  const [expandedStory, setExpandedStory] = useState({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loginPopoverTarget, setLoginPopoverTarget] = useState(null);
  const [mobileLoginView, setMobileLoginView] = useState(false);
  const toggleStory = (story) => setExpandedStory(prev => ({ ...prev, [story]: !prev[story] }));
  const sunRef = useRef(null);
  const headerRef = useRef(null);
  const lastScrollY = useRef(0);

  const launchReady = test || true;

  useEffect(() => { document.title = "Welcome — myPatientSpace"; }, []);

  useEffect(() => {
    const currentUrl = window.location.href;
    if (!currentUrl.includes("melanoma")) {
      history.push("/login" + window.location.search);
    } else {
      setShowContent(true);
    }
  }, []);

  useEffect(() => {
    if (!showContent) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, { threshold: 0.15 });

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [showContent]);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    // Smart Header Logic
    if (headerRef.current) {
      const delta = currentScrollY - lastScrollY.current;
      // Provide a buffer threshold so tiny bounces don't instantly trigger a hide/show
      if (Math.abs(delta) > 10) {
        if (delta > 0 && currentScrollY > 85) {
          headerRef.current.classList.add("header-hidden");
        } else {
          headerRef.current.classList.remove("header-hidden");
        }
        lastScrollY.current = currentScrollY;
      }
    }

    // Sun Animation Logic
    if (!sunRef.current) return;
    // ensure docHeight > 0 to avoid division by zero
    const docHeight = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const progress = Math.min(currentScrollY / docHeight, 1);

    const isMobile = window.innerWidth <= 768;

    // Scale dynamically depending on desktop/mobile
    const scaleDrop = isMobile ? 0.4 : 0.6; // Shrinks less on mobile since base size is smaller
    const scale = 1 - progress * scaleDrop;

    // Shift to left
    const rightStart = isMobile ? -35 : -10;
    const rightEnd = isMobile ? 100 : 70;
    const right = rightStart + progress * (rightEnd - rightStart);

    // Shift downwards to reach the 'bottom left' properly on mobile
    const topStart = isMobile ? -5 : -10;
    const topEnd = isMobile ? 50 : -10; // Keep desktop at -10% as it was working fine for user
    const top = topStart + progress * (topEnd - topStart);

    const opacity = 0.5 + 0.3 * Math.sin(progress * Math.PI);

    sunRef.current.style.transform = "scale(" + scale + ")";
    sunRef.current.style.right = right + "%";
    sunRef.current.style.top = top + "%";
    sunRef.current.style.opacity = opacity;
  }, []);

  useEffect(() => {
    if (!showContent) return;
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showContent, handleScroll]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      setMobileLoginView(false);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  if (!showContent) return null;

  if (!launchReady) {
    return (
      <main id="main-layout" className="coming-soon-wrapper">
        <div className="coming-soon-card">
          <img
            src={"/melanoma/logo.png"}
            alt="melanoma logo"
            width={100}
            height={130}
            className="mb-4 mx-auto"
            style={{ display: "block" }}
          />
          <h1 className="mb-2" style={{ color: "#0ea5e9", fontWeight: "800", letterSpacing: "-0.5px" }}>
            MELANOMA IRELAND
          </h1>
          <h2 className="mb-4" style={{ fontWeight: "700", color: "#0f172a" }}>Coming Soon</h2>
          <p className="text-muted mb-4" style={{ fontSize: "1.125rem", lineHeight: "1.7" }}>
            We're working hard to bring you a comprehensive melanoma support
            platform. Check back soon for trusted information, support
            resources, and community connections.
          </p>
          <div style={{ display: "inline-block", padding: "0.5rem 1.25rem", background: "#f0f9ff", color: "#0284c7", borderRadius: "50px", fontWeight: "600" }}>
            Stay tuned for updates
          </div>
        </div>
      </main>
    );
  }

  return (
    <main id="main-layout" className="melanoma-landing">
      {/* Header Navigation */}
      <header className="melanoma-header" ref={headerRef}>
        <div
          className="melanoma-logo-container"
          onClick={() => {
            setIsMobileMenuOpen(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <img
            src={"/melanoma/logo.png"}
            alt="melanoma logo"
            width={40}
            height={52}
          />
          <div className="melanoma-logo-text-stack">
            <span>MELANOMA</span>
            <span>IRELAND</span>
          </div>
        </div>

        <nav className="melanoma-nav-links">
          <span className="melanoma-nav-link" onClick={() => scrollToSection('resources')}>Resources</span>
          <span className="melanoma-nav-link" onClick={() => scrollToSection('education')}>Education</span>
          <span className="melanoma-nav-link" onClick={() => scrollToSection('stories')}>Stories</span>
          <span className="melanoma-nav-link" onClick={() => scrollToSection('support-services')}>Support</span>
          <span className="melanoma-nav-link" onClick={() => scrollToSection('about')}>About</span>
        </nav>

        <div className="melanoma-header-actions d-none d-md-flex">
          <Button
            id="melanoma-header-login-btn-desktop"
            className="btn-header-login"
            onClick={() => setLoginPopoverTarget("desktop")}
          >
            <Text content={Messages.login} />
          </Button>
          <Button
            className="btn-header-signup"
            onClick={() => history.push("/signup/Melanoma?region=eu")}
          >
            Sign up free
          </Button>
        </div>

        <button
          className="mobile-menu-toggle d-md-none"
          onClick={() => {
            if (mobileLoginView) {
              setMobileLoginView(false);
            } else {
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }
          }}
          aria-label={mobileLoginView ? intl.formatMessage(Messages.back) : "Toggle menu"}
        >
          {mobileLoginView ? (
            <ArrowLeft size={32} />
          ) : isMobileMenuOpen ? (
            <X size={32} />
          ) : (
            <Menu size={32} />
          )}
        </button>

        {/* Mobile Menu Overlay */}
        <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
          <div style={{ position: 'absolute', top: '1rem', left: '1.25rem', display: 'flex', alignItems: 'center' }}>
            <img src={"/melanoma/logo.png"} alt="melanoma logo" width={40} height={52} />
            <div className="melanoma-logo-text-stack mx-2">
              <span>MELANOMA</span>
              <span>IRELAND</span>
            </div>
          </div>
          {mobileLoginView ? (
            <div className="mobile-menu-content mobile-login-subpage">
              <h3 className="mobile-login-title">
                <Text content={Messages.login_to_account} />
              </h3>
              <div className="mobile-login-form-wrap mt-3">
                <MelanomaLoginForm onClose={() => setMobileLoginView(false)} />
              </div>
            </div>
          ) : (
            <div className="mobile-menu-content">
              <span className="mobile-nav-link" onClick={() => scrollToSection('resources')}>Resources</span>
              <span className="mobile-nav-link" onClick={() => scrollToSection('education')}>Education</span>
              <span className="mobile-nav-link" onClick={() => scrollToSection('stories')}>Stories</span>
              <span className="mobile-nav-link" onClick={() => scrollToSection('support-services')}>Support</span>
              <span className="mobile-nav-link" onClick={() => scrollToSection('about')}>About</span>
              <div className="mobile-menu-actions mt-4 pt-4" style={{ borderTop: "1px solid #eaeaea" }}>
                <Button
                  id="melanoma-header-login-btn-mobile"
                  className="btn-header-login w-100 mb-3 py-3"
                  onClick={() => setMobileLoginView(true)}
                  style={{ fontSize: "1.1rem" }}
                >
                  <Text content={Messages.login} />
                </Button>
                <Button
                  className="btn-header-signup w-100 py-3"
                  onClick={() => history.push("/signup/Melanoma?region=eu")}
                  style={{ fontSize: "1.1rem" }}
                >
                  Sign up free
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="sun-animation" ref={sunRef}>
        <div className="sun-core"></div>
        <div className="sun-rays"></div>
      </div>

      {/* Main Content */}
      <div className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg="6" md="12" className="mb-5 mb-lg-0 pr-lg-5 animate-on-scroll">
              <span className="hero-badge">Melanoma Support in Ireland</span>
              <h1 className="hero-title">
                Your trusted information and support hub
              </h1>
              <h3 className="hero-subtitle">
                Supporting You at Every Stage of Your Melanoma Journey.
              </h3>
              <p className="hero-description">
                This platform is designed for people across Ireland affected by
                melanoma. Here, you'll find trusted information, personal stories,
                and support resources — all created in partnership with Clinical
                Teams and Patient Advocacy Group.
              </p>
              <div className="hero-actions">
                <Button
                  onClick={() => scrollToSection('about')}
                  className="btn-primary-custom"
                >
                  Learn More
                </Button>
              </div>
            </Col>
            <Col lg="6" md="12" className="d-none d-lg-block animate-on-scroll delay-200">
              <div className="hero-image-wrapper">
                <div className="hero-image-backdrop"></div>
                <img
                  src={"/melanoma/about.jpg"}
                  alt="Melanoma Support Community"
                  className="hero-image"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <div className="features-section" id="resources">
        <Container>
          <h2 className="about-title mb-5 text-center animate-on-scroll">Comprehensive Resources</h2>
          <Row>
            <Col md="4" className="mb-4 animate-on-scroll delay-100">
              <Card className="feature-card">
                <CardBody>
                  <div className="feature-icon-wrapper">
                    <BookOpen size={42} strokeWidth={1.5} />
                  </div>
                  <h3 className="feature-title">Trusted Information</h3>
                  <p className="feature-text">
                    Access clinically reviewed, easy-to-understand information about melanoma, including diagnosis, staging, treatments, and care pathways tailored for patients in Ireland.
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md="4" className="mb-4 animate-on-scroll delay-200">
              <Card className="feature-card">
                <CardBody>
                  <div className="feature-icon-wrapper">
                    <Users size={42} strokeWidth={1.5} />
                  </div>
                  <h3 className="feature-title">Patient Stories &amp; Community</h3>
                  <p className="feature-text">
                    Explore real stories from people affected by melanoma and feel less alone in your journey. Learn from shared experiences, insights, and perspectives from the community.
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md="4" className="mb-4 animate-on-scroll delay-300">
              <Card className="feature-card">
                <CardBody>
                  <div className="feature-icon-wrapper">
                    <Shield size={42} strokeWidth={1.5} />
                  </div>
                  <h3 className="feature-title">Support at Every Stage</h3>
                  <p className="feature-text">
                    Find guidance and support for every step of your journey &mdash; from diagnosis to treatment and beyond, with resources designed to support both patients and their loved ones.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <MelanomaEducation />

      <div className="stories-section" id="stories">
        <Container>
          <h2 className="about-title mb-5 text-center animate-on-scroll">Patient Stories</h2>
          <Row>
            <Col lg="8" className="mx-auto mb-4 animate-on-scroll delay-100">
              <Card className="story-compact-card">
                <CardBody className="story-compact-body">
                  <div className="story-top-row">
                    <div className="story-author">
                      <div className="story-avatar">K</div>
                      <div className="story-author-info">
                        <strong>Kieran</strong>
                        <span className="story-role">Survivor</span>
                      </div>
                    </div>
                    <p className="story-quote">
                      "Trust the professionalism of the medical staff caring for you. Speaking openly with family and friends made all the difference."
                    </p>
                  </div>
                  <Collapse isOpen={expandedStory.kieran}>
                    <div className="story-details">
                      <div className="mb-3 d-flex flex-wrap gap-2">
                        <span className="story-badge">Age: 67</span>
                        <span className="story-badge">Retired</span>
                        <span className="story-badge">Midlands, Ireland</span>
                      </div>
                      <img src="/melanoma/keiran.jpg" alt="A sleek road bicycle leaning against a traditional stone wall in the lush Irish countryside" className="img-fluid rounded mb-4 shadow-sm" style={{ maxHeight: "250px", width: "100%", objectFit: "cover" }} />
                      <p><strong>Hobby:</strong> Cycling (100km/week)</p>
                      <p><strong>Discovery:</strong> December 2020 &mdash; thought it was an insect bite; GP said it was more serious</p>
                      <p><strong>Information gaps:</strong> None &mdash; felt fully informed by GP</p>
                      <p><strong>Challenges:</strong> Coming to terms with severity; understanding steps forward</p>
                      <p><strong>Treatment:</strong> 3 procedures including lymph node removal (left armpit); 26 weeks of immunotherapy (ran smoothly)</p>
                      <div className="story-advice-box">
                        <strong>Advice:</strong> <span style={{ fontStyle: "italic" }}>Trust the professionalism of the medical staff caring for you</span>
                      </div>
                      <p><strong>Coping:</strong> Speaking openly with family and friends; trusting the process</p>
                    </div>
                  </Collapse>
                  <Button color="link" className="story-expand-btn" onClick={() => toggleStory('kieran')}>
                    {expandedStory.kieran ? "Read less" : "Read full story"}
                  </Button>
                </CardBody>
              </Card>
            </Col>

            <Col lg="8" className="mx-auto mb-4 animate-on-scroll delay-200">
              <Card className="story-compact-card">
                <CardBody className="story-compact-body">
                  <div className="story-top-row">
                    <div className="story-author">
                      <div className="story-avatar">M</div>
                      <div className="story-author-info">
                        <strong>Miriam</strong>
                        <span className="story-role">Survivor</span>
                      </div>
                    </div>
                    <p className="story-quote">
                      "Remain calm; take each day as it comes; do something you enjoy. Be aware some people will distance themselves, others will step up."
                    </p>
                  </div>
                  <Collapse isOpen={expandedStory.miriam}>
                    <div className="story-details">
                      <div className="mb-3 d-flex flex-wrap gap-2">
                        <span className="story-badge">Age: 49</span>
                        <span className="story-badge">Married, 3 teenagers, a dog</span>
                        <span className="story-badge">Amateur triathlete</span>
                      </div>
                      <img src="/melanoma/miriam.jpg" alt="Bicycle helmet and running shoes resting by a sunny window" className="img-fluid rounded mb-4 shadow-sm" style={{ maxHeight: "250px", width: "100%", objectFit: "cover" }} />
                      <p><strong>Discovery:</strong> Age 45 &mdash; felt discomfort in right armpit while cycling; lump found; referred to St James's Hospital; biopsy confirmed melanoma (not breast cancer)</p>
                      <p><strong>Diagnosis:</strong> Stage 3 melanoma with no known primary source (cancer only in lymph nodes, skin clear)</p>
                      <p><strong>Information gaps:</strong> Was initially directed to breast clinic, had to "start again" with dermatology &mdash; found navigating separate hospital departments confusing and distressing</p>
                      <p><strong>Challenges:</strong> Deciding who/how to share the news, especially with children. Irish Cancer Society provided helpful advice on talking to children</p>
                      <h5 className="mt-4 mb-3" style={{ fontWeight: "700" }}>Treatment Timeline:</h5>
                      <ul style={{ paddingLeft: "1.5rem", marginBottom: "2rem" }}>
                        <li className="mb-2"><strong>March 2018:</strong> Lymph node removal surgery</li>
                        <li className="mb-2"><strong>June 2018:</strong> Local radiation completed</li>
                        <li className="mb-2"><strong>Post-radiation:</strong> Offered Interferon (&lt; 4% efficacy, severe side effects) &mdash; chose watch &amp; wait (70% recurrence chance)</li>
                        <li className="mb-2"><strong>Early 2019:</strong> Scan showed 2cm liver tumour &mdash; progressed to Stage 4</li>
                        <li className="mb-2"><strong>2019:</strong> Started combined ipilimumab + nivolumab (4 infusions planned); after 3 doses, admitted to ICU with Addisonian crisis (lifelong adrenal condition)</li>
                        <li className="mb-2"><strong>Post-recovery:</strong> Restarted nivolumab only; liver tumour stubborn</li>
                        <li className="mb-2"><strong>Oct 2019:</strong> Paused immunotherapy; stereotactic radiation on liver</li>
                        <li className="mb-2"><strong>Jan 2020:</strong> Restarted immunotherapy</li>
                        <li className="mb-2"><strong>April 2020:</strong> No signs of active disease</li>
                        <li className="mb-2"><strong>Dec 2021:</strong> Stopped nivolumab treatment; currently doing well off treatment</li>
                      </ul>
                      <div className="story-advice-box">
                        <strong>Advice:</strong> <span style={{ fontStyle: "italic" }}>Remain calm; take each day as it comes; do something you enjoy; join local (non-US) online support groups; be aware some people will distance themselves, others will step up</span>
                      </div>
                      <p><strong>Coping:</strong> Training (swimming, biking, running); support network for laughter, crying, and venting</p>
                    </div>
                  </Collapse>
                  <Button color="link" className="story-expand-btn" onClick={() => toggleStory('miriam')}>
                    {expandedStory.miriam ? "Read less" : "Read full story"}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <div className="support-section py-5" style={{ backgroundColor: "#FAFAFA" }} id="support-services">
        <Container>
          <Row className="align-items-center mb-5 mt-5 text-center text-lg-left">
            <Col lg="8" className="order-2 order-lg-1 animate-on-scroll">
              <h2 className="about-title mb-0" style={{ color: "#000" }}>Support Services in Ireland</h2>
            </Col>
            <Col lg="4" className="order-1 order-lg-2 text-center text-lg-right mb-4 mb-lg-0 animate-on-scroll delay-100">
              <img src="/melanoma/support_hands.png" alt="Joined hands in a circle of support" className="img-fluid rounded-circle shadow-sm" style={{ width: "140px", height: "140px", objectFit: "cover", border: "4px solid #fff", margin: "0 auto" }} />
            </Col>
          </Row>

          <Row>
            <Col lg="6" className="mb-4 animate-on-scroll delay-100">
              <Card className="feature-card h-100" style={{ textAlign: "left", padding: "2.5rem 2rem" }}>
                <h4 style={{ fontWeight: "800", color: "#0284c7", marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "10px" }}>
                  <Stethoscope size={28} style={{ flexShrink: 0 }} /> Irish Cancer Society
                </h4>
                <ul style={{ paddingLeft: "1.5rem", fontSize: "1.05rem", lineHeight: "1.8", color: "#475569", margin: 0 }}>
                  <li className="mb-2"><strong>Freephone Cancer Support Line:</strong> 1800 200 700 &mdash; specialist cancer nurses</li>
                  <li className="mb-2"><strong>Daffodil Centres:</strong> Located in 13 hospitals nationwide</li>
                  <li className="mb-2"><strong>Survivor Support Programme:</strong> Matches newly diagnosed patients with survivors for 1-on-1 guidance</li>
                  <li className="mb-2"><strong>Counselling Services:</strong> Free counselling</li>
                  <li className="mb-2"><strong>Transport to Treatment:</strong> Travel2Care financial grants</li>
                  <li className="mb-2"><strong>Night Nursing:</strong> In-home end-of-life care</li>
                  <li className="mb-2"><strong>Information Resources:</strong> Booklets, leaflets, online guides; Melanoma Patient Booklet (~57 pages)</li>
                </ul>
              </Card>
            </Col>

            <Col lg="6" className="mb-4 animate-on-scroll delay-200">
              <Card className="feature-card h-100" style={{ textAlign: "left", padding: "2.5rem 2rem" }}>
                <h4 style={{ fontWeight: "800", color: "#0284c7", marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "10px" }}>
                  <FileCheck size={28} style={{ flexShrink: 0 }} /> HSE National Cancer Control Programme
                </h4>
                <ul style={{ paddingLeft: "1.5rem", fontSize: "1.05rem", lineHeight: "1.8", color: "#475569", marginBottom: "2.5rem" }}>
                  <li className="mb-2">Updated melanoma guidelines (Aug–Oct 2024)</li>
                  <li className="mb-2"><strong>Focus:</strong> improved care consistency, earlier recurrence detection, better treatment planning</li>
                  <li className="mb-2">Developed with input from patients, doctors, nurses, researchers</li>
                </ul>

                <h4 style={{ fontWeight: "800", color: "#0284c7", marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "10px" }}>
                  <HeartHandshake size={28} style={{ flexShrink: 0 }} /> Marie Keating Foundation
                </h4>
                <ul style={{ paddingLeft: "1.5rem", fontSize: "1.05rem", lineHeight: "1.8", color: "#475569", margin: 0 }}>
                  <li className="mb-2">National charity for cancer awareness, education, and support</li>
                  <li className="mb-2">Services are free, funded by fundraising</li>
                </ul>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg="6" className="mb-4 animate-on-scroll delay-100">
              <Card className="feature-card h-100" style={{ textAlign: "left", padding: "2rem 1.5rem" }}>
                <h5 style={{ fontWeight: "800", color: "#0284c7", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "10px" }}>
                  <Link size={24} style={{ flexShrink: 0 }} /> Other Resources
                </h5>
                <ul style={{ paddingLeft: "1.5rem", fontSize: "1rem", lineHeight: "1.7", color: "#475569", margin: 0 }}>
                  <li className="mb-2"><strong>Irish Skin Foundation:</strong> General skin health &amp; skin cancer support</li>
                  <li className="mb-2"><strong>Sharon Rice O'Beirne Melanoma Trust:</strong> Melanoma-specific support</li>
                  <li className="mb-2"><strong>Irish Red Cross:</strong> Skin Camouflage Training</li>
                  <li className="mb-2"><strong>National Advocacy Service (NAS):</strong> Free, confidential, independent advocacy for public hospitals</li>
                </ul>
              </Card>
            </Col>

            <Col lg="6" className="mb-4 animate-on-scroll delay-200">
              <Card className="feature-card h-100" style={{ textAlign: "left", padding: "2rem 1.5rem", backgroundColor: "#FFEB3B", border: "none" }}>
                <h5 style={{ fontWeight: "800", color: "#0284c7", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "10px" }}>
                  <Lightbulb size={24} style={{ flexShrink: 0 }} /> Tips for Patients &amp; Families
                </h5>
                <ul style={{ paddingLeft: "1.5rem", fontSize: "1rem", lineHeight: "1.7", color: "#000", margin: 0 }}>
                  <li className="mb-2">Start with the Irish Cancer Society (helpline or Daffodil Centre)</li>
                  <li className="mb-2">Request the free Melanoma Booklet</li>
                </ul>
              </Card>
            </Col>
          </Row>

          <Row className="mt-4 mb-5 animate-on-scroll">
            <Col md="12">
              <Card className="feature-card" style={{ textAlign: "left", padding: "3rem" }}>
                <h4 style={{ fontWeight: "800", color: "#0284c7", marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: "10px" }}>
                  <Stethoscope size={28} style={{ flexShrink: 0 }} /> Find Support Near You
                </h4>
                <p style={{ fontSize: "1.1rem", color: "#475569", marginBottom: "1rem", fontWeight: "600" }}>
                  Specialist nursing support available across multiple hospitals.
                </p>
                <p style={{ fontSize: "1.05rem", color: "#475569", lineHeight: "1.8", marginBottom: "1.5rem" }}>
                  You are not alone. Across the hospitals below, specialised Skin Cancer Clinical Nurse Specialists are available to support you, answer your questions, and guide you through your melanoma journey.
                </p>
                <Row>
                  <Col md="6">
                    <ul style={{ paddingLeft: "1.5rem", fontSize: "1.05rem", lineHeight: "2", color: "#475569", margin: 0 }}>
                      <li>Mater University Hospital</li>
                      <li>St James' Hospital</li>
                      <li>Beaumont Hospital</li>
                      <li>St Vincent's Hospital</li>
                      <li>Our Lady of Lourdes Hospital, Drogheda</li>
                      <li>Galway University Hospital</li>
                    </ul>
                  </Col>
                  <Col md="6">
                    <ul style={{ paddingLeft: "1.5rem", fontSize: "1.05rem", lineHeight: "2", color: "#475569", margin: 0 }}>
                      <li>University Hospital Limerick</li>
                      <li>South Infirmary Victoria Hospital, Cork</li>
                      <li>Waterford Hospital</li>
                      <li>Tallaght University Hospital</li>
                      <li>Roscommon Hospital</li>
                    </ul>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <div className="about-section" id="about">
        <Container>
          <div className="animate-on-scroll">
            <h2 className="about-title">About Melanoma Ireland</h2>
            <div className="text-left mx-auto" style={{ maxWidth: "1000px", color: "#e2e8f0", fontSize: "1.15rem", lineHeight: "1.8" }}>
              <p className="mb-4"><strong>Mission:</strong> Empower patients, families, and caregivers with reliable information and support at every stage of the melanoma journey.</p>
              <p className="mb-5"><strong>Nature:</strong> Not-for-profit support and advocacy initiative, run by Irish melanoma patients, for patients.</p>

              <Row className="mt-5 mb-5" style={{ textAlign: "left" }}>
                <Col md="6" className="mb-4 pr-lg-5">
                  <h4 style={{ color: "#FFEB3B", fontWeight: "800", marginBottom: "1.5rem" }}>Target Audience</h4>
                  <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
                    <li className="mb-2">People diagnosed with melanoma</li>
                    <li className="mb-2">Family members, partners, and caregivers</li>
                    <li className="mb-2">Anyone looking to understand melanoma and its impact</li>
                  </ul>
                </Col>
                <Col md="6" className="mb-4">
                  <h4 style={{ color: "#FFEB3B", fontWeight: "800", marginBottom: "1.5rem" }}>What the Platform Offers</h4>
                  <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
                    <li className="mb-2">Clear, clinically reviewed information about melanoma</li>
                    <li className="mb-2">Guidance on diagnosis, staging, and treatment</li>
                    <li className="mb-2">Support for emotional wellbeing and mental health</li>
                    <li className="mb-2">Real stories from people who have experienced melanoma</li>
                    <li className="mb-2">Resources to help navigate conversations and decisions</li>
                  </ul>
                </Col>
              </Row>
            </div>
          </div>

          <div id="support" className="mt-5 pt-5 animate-on-scroll delay-200" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <h3 className="join-title mb-4">Need Support? Join Us Today</h3>
            <p className="about-text mx-auto" style={{ fontSize: "1.1rem" }}>
              Sign up for free to unlock all resources and support.
            </p>
            <Button
              onClick={() => history.push("/signup/Melanoma?region=eu")}
              className="btn-secondary-custom mt-4"
            >
              Sign up free
            </Button>
          </div>
        </Container>
      </div>

      <Container className="d-block d-lg-none pt-5 mt-4 pb-2 animate-on-scroll">
        <div className="hero-image-wrapper mx-auto" style={{ maxWidth: "450px" }}>
          <div className="hero-image-backdrop"></div>
          <img
            src={"/melanoma/about.jpg"}
            alt="Melanoma Support Community"
            className="hero-image"
          />
        </div>
      </Container>

      <div className="mt-auto">
        <Footer />
      </div>

      <MelanomaLoginPopover
        isOpen={loginPopoverTarget === "desktop"}
        target="melanoma-header-login-btn-desktop"
        placement="bottom-end"
        onClose={() => setLoginPopoverTarget(null)}
      />

    </main>
  );
};

export default Home;